import http from './httpClient';

import type { CaasCluster } from '@/types/productTypes';

const Provision = {
  fetchClouds: () => {
    return http.get(`${import.meta.env.VITE_PROVISION_V0_BASE}/clouds`);
  },

  fetchCloudDetails: (juiceGroup: string, cloudId: string) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V0_BASE}/clouds/${cloudId}`, {
      params: { juiceGroup },
    });
  },

  fetchHosts: (cloudName: string) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/capacity/${cloudName}`);
  },

  createOsProject: (data: Record<string, unknown>) => {
    return http.post(`${import.meta.env.VITE_PROVISION_V1_BASE}/os-projects`, data);
  },

  validateOsProjectName: (osProjectName: string, cloudId: string, juiceGroup: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/os-projects/validate-name`, {
      params: { osProjectName, cloudId, juiceGroup },
    });
  },

  fetchOsProjectsV1: (juiceGroup: unknown, juiceOsProjects: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/os-projects`, {
      params: { juiceGroup, juiceOsProjects },
    });
  },

  fetchOsProjectDetails: (juiceGroup: string, cloudId: string, osprojectName: string) => {
    return http.get(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/os-projects/details/${juiceGroup}/${cloudId}/${osprojectName}`
    );
  },

  deleteOsProject: (cloudId: string, name: string) => {
    return http.delete(`${import.meta.env.VITE_PROVISION_V1_BASE}/os-projects/${cloudId}/${name}`);
  },

  patchOsProject: (juiceGroup: string, cloudId: string, name: string, data: unknown) => {
    return http.patch(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/os-projects/${juiceGroup}/${cloudId}/${name}`,
      data
    );
  },

  fetchCaasClusterCheck: (name: string) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/caas/cluster/check`, {
      params: { name },
    });
  },

  fetchCaasClustersV1: (params: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/caas/clusters`, { params });
  },

  fetchCaasClusterNodes: (clusterName: string) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/caas/nodes/${clusterName}`);
  },

  fetchClusterDetailsV1: (clusterName: string) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/caas/clusters/${clusterName}`);
  },

  fetchBareMetalClustersV1: (juiceGroup: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/bare-metal/clusters`, {
      params: { juiceGroup },
    });
  },

  fetchClusterConfigV1: (clusterName: CaasCluster['name']) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/caas/clusters/kubeconfig`, {
      params: { clusterName },
    });
  },

  updateCloudPassword: (juiceGroup: unknown, cloudId: string, password: string) => {
    return http.put(`${import.meta.env.VITE_PROVISION_V0_BASE}/osUsers/${cloudId}`, {
      juiceGroup,
      password,
    });
  },

  fetchVirtualClouds: (data: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/virtual-clouds`, { params: data });
  },

  createVirtualClouds: (forecasts: unknown) => {
    return http.post(`${import.meta.env.VITE_PROVISION_V1_BASE}/virtual-clouds`, {
      virtualClouds: forecasts,
    });
  },

  fetchNetworkPolicies: (juiceGroup: unknown, cloudId: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/networks/policies`, {
      params: { juiceGroup, cloudId },
    });
  },

  fetchForecasts: (juiceGroup: unknown, cloudId: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/forecast`, {
      params: { juiceGroup, cloudId },
    });
  },

  fetchForecastCapacity: (juiceGroup: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/forecast/aggregates-capacity`, {
      params: { juiceGroup },
    });
  },

  fetchOSVirtualResources: (juiceGroup: unknown, cloudIds: unknown) => {
    return http.get(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/reports/os/virtual-resources/summary`,
      { params: { juiceGroup, cloudIds } }
    );
  },

  fetchInstanceDetails: (params: unknown) => {
    return http.get(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/reports/os/virtual-resources/instances/usage`,
      { params }
    );
  },

  fetchJuiceGroupByName: (groupname: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group`, {
      params: { groupname },
    });
  },

  fetchJuiceGroupInfo: (juiceGroup: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info`, {
      params: juiceGroup,
    });
  },

  fetchContacts: (params: unknown) => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info/contacts`, {
      params,
    });
  },

  addContact: (data: unknown) => {
    return http.post(`${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info/contacts`, data);
  },

  editContact: (data: unknown, contactId: string) => {
    return http.put(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info/contacts/${contactId}`,
      data
    );
  },

  removeContact: (contactId: string) => {
    return http.delete(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info/contacts/${contactId}`
    );
  },

  updateGeoRedundancy: (data: unknown, juiceGroup: string) => {
    return http.put(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info/${juiceGroup}/georedundancy`,
      data
    );
  },

  addExternalService: (data: unknown, juiceGroup: string) => {
    return http.post(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info/${juiceGroup}/external-service`,
      data
    );
  },

  deleteExternalService: (juiceGroup: string, externalService: string) => {
    return http.delete(
      `${import.meta.env.VITE_PROVISION_V1_BASE}/juice-group-info/${juiceGroup}/external-service/${externalService}`
    );
  },

  fetchTags: () => {
    return http.get(`${import.meta.env.VITE_PROVISION_V1_BASE}/tags`);
  },
};

export default Provision;
